// A fragment's import base (`ImportBase`, SuiteModel): what Scrivener said at the last import, kept on the fragment's root
// key `importBase` as JSON exactly as the Swift's `JSONEncoder` with `.sortedKeys` writes it — `MetadataValue` as the
// synthesised enum form `{"text":{"_0":…}}`, dates as seconds since 2001 — so a project imported here re-imports
// three-way on a Mac, and the other way round.
import * as A from '@automerge/automerge';
import type { MetadataValue } from '../contract.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { fromReferenceSeconds, sortedJSON, toReferenceSeconds } from '../review/json.ts';

export interface ImportBase {
  title: string;
  text: string;
  synopsis: string;
  notes: string;
  includeInCompile: boolean;
  metadata: Record<string, MetadataValue>;
  /** Whether the item was in Scrivener's Trash (absent in bases recorded before this was kept). */
  trashed?: boolean;
  /** SHA-256s of the attachments the importer took from the source (absent before this was kept). */
  attachments?: string[];
  /** Ids of the comments the importer made (absent before this was kept), so one the writer deleted isn't re-added. */
  comments?: string[];
}

export const IMPORT_BASE_KEY = 'importBase';

/** The Swift's synthesised `Codable` form of a `MetadataValue`. */
function encodeValue(v: MetadataValue): unknown {
  switch (v.type) {
    case 'text': return { text: { _0: v.value } };
    case 'number': return { number: { _0: v.value } };
    case 'boolean': return { boolean: { _0: v.value } };
    case 'date': return { date: { _0: toReferenceSeconds(v.value) } };
  }
}

function decodeValue(raw: unknown): MetadataValue | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, { _0?: unknown }>;
  if (o.text && typeof o.text._0 === 'string') return { type: 'text', value: o.text._0 };
  if (o.number && typeof o.number._0 === 'number') return { type: 'number', value: o.number._0 };
  if (o.boolean && typeof o.boolean._0 === 'boolean') return { type: 'boolean', value: o.boolean._0 };
  if (o.date && typeof o.date._0 === 'number') return { type: 'date', value: fromReferenceSeconds(o.date._0) };
  return null;
}

/** The base as the Swift stores it: sorted keys, optionals left out when absent. */
export function encodeImportBase(base: ImportBase): string {
  const metadata: Record<string, unknown> = {};
  for (const key of Object.keys(base.metadata).sort()) metadata[key] = encodeValue(base.metadata[key]);
  return sortedJSON({
    title: base.title, text: base.text, synopsis: base.synopsis, notes: base.notes, includeInCompile: base.includeInCompile, metadata,
    ...(base.trashed === undefined ? {} : { trashed: base.trashed }),
    ...(base.attachments === undefined ? {} : { attachments: base.attachments }),
    ...(base.comments === undefined ? {} : { comments: base.comments }),
  }, toReferenceSeconds);
}

export function decodeImportBase(json: string): ImportBase | null {
  let raw: unknown;
  try { raw = JSON.parse(json); } catch { return null; }
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const text = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : null);
  const title = text('title'), body = text('text'), synopsis = text('synopsis'), notes = text('notes');
  if (title === null || body === null || synopsis === null || notes === null || typeof o.includeInCompile !== 'boolean') return null;
  const metadata: Record<string, MetadataValue> = {};
  if (o.metadata && typeof o.metadata === 'object') {
    for (const [k, v] of Object.entries(o.metadata as Record<string, unknown>)) {
      const value = decodeValue(v);
      if (value) metadata[k] = value;
    }
  }
  const strings = (k: string) => (Array.isArray(o[k]) ? (o[k] as unknown[]).filter((s): s is string => typeof s === 'string') : undefined);
  return {
    title, text: body, synopsis, notes, includeInCompile: o.includeInCompile, metadata,
    ...(typeof o.trashed === 'boolean' ? { trashed: o.trashed } : {}),
    ...(strings('attachments') ? { attachments: strings('attachments') } : {}),
    ...(strings('comments') ? { comments: strings('comments') } : {}),
  };
}

/** `FragmentDocument.importBase()`. */
export function readImportBase(doc: A.Doc<unknown>, heads?: string[]): ImportBase | null {
  const json = new SwiftReader(doc, heads).string(ROOT, IMPORT_BASE_KEY);
  return json ? decodeImportBase(json) : null;
}

/** `MetadataValue ==`. */
export function sameValue(a: MetadataValue | null | undefined, b: MetadataValue | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  if (a.type !== b.type) return false;
  if (a.type === 'date' && b.type === 'date') return a.value.getTime() === b.value.getTime();
  return a.value === b.value;
}
